import React, { useState, useEffect } from 'react';
import { EventsService, type EventWithClientDTO } from '@/services/EventsService';
import { AttendeesService } from '@/services/AttendeesService';
import { ResearchService } from '@/services/ResearchService';
import { ListingBlock } from '@/components/ui';
import { Spinner } from '@/components/ui/Spinner/Spinner';
import { EmptyState } from '@/components/ui/EmptyState/EmptyState';
import { Input } from '@/components/ui/Input/Input';
import { FileText, Search, Users, MapPin } from 'lucide-react';

interface EventSelectorProps {
  onEventSelect: (eventId: string) => void;
}

interface EventWithCounts extends EventWithClientDTO {
  attendees_count: number;
  destinations_count: number;
}

export function EventSelector({ onEventSelect }: EventSelectorProps) {
  const [events, setEvents] = useState<EventWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const eventsService = new EventsService();
  const attendeesService = new AttendeesService();
  const researchService = new ResearchService();

  useEffect(() => {
    loadEvents();
  }, []);

  async function loadEvents() {
    try {
      setLoading(true);
      const data = await eventsService.listAll();
      
      // Fetch counts for each event in parallel
      const eventsWithCounts = await Promise.all(
        data.map(async (event) => {
          try {
            const [attendeesData, destinations] = await Promise.all([
              attendeesService.listByEvent(event.id, 1, 1), // Just get first page to get total count
              researchService.listDestinations(event.id),
            ]);
            
            return {
              ...event,
              attendees_count: attendeesData.pagination.totalItems,
              destinations_count: destinations.length,
            };
          } catch (error) {
            console.error(`Failed to load counts for event ${event.id}:`, error);
            return {
              ...event,
              attendees_count: 0,
              destinations_count: 0,
            };
          }
        })
      );
      
      setEvents(eventsWithCounts);
    } catch (error) {
      console.error('Failed to load events:', error);
    } finally {
      setLoading(false);
    }
  }

  const filteredEvents = events.filter(event =>
    event.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    event.client_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12">
        <EmptyState
          icon={<FileText className="w-12 h-12" />}
          title="No Events Found"
          description="Create an event for a client first, then start your research and proposal workflow."
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">Select an Event</h2>
        <p className="text-muted-foreground">
          Choose the event you want to research venues for and create a proposal.
        </p>
      </div>

      <div className="mb-6">
        <Input
          placeholder="Search events by name or client..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          leftIcon={<Search className="w-4 h-4" />}
        />
      </div>

      <div className="space-y-3">
        {filteredEvents.length === 0 ? (
          <EmptyState
            icon={<Search className="w-8 h-8" />}
            title="No matching events"
            description="Try adjusting your search query"
          />
        ) : (
          filteredEvents.map((event) => (
            <ListingBlock
              key={event.id}
              title={event.name}
              subtitle={`Client: ${event.client_name}`}
              description={event.description || undefined}
              metaItems={[
                {
                  icon: <Users className="w-4 h-4 text-primary-600" />,
                  label: 'Attendees',
                  value: event.attendees_count.toString(),
                },
                {
                  icon: <MapPin className="w-4 h-4 text-primary-600" />,
                  label: 'Destinations',
                  value: event.destinations_count.toString(),
                },
              ]}
              onClick={() => onEventSelect(event.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

